import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Flame,
  Gavel,
  ListChecks,
  Timer,
  Undo2,
} from 'lucide-react';
import { formatDuration, formatHours, formatPercent, formatTaskReference } from '@ssn/shared';
import { useDashboard } from '@/lib/queries';
import { useSession } from '@/session/SessionProvider';
import { PageHeader, Panel, StatTile } from '@/components/ui/Panel';
import { ErrorState, Callout, LoadingBlock, EmptyState } from '@/components/ui/Feedback';
import { LinkButton } from '@/components/ui/Button';
import { UserChip } from '@/components/ui/Avatar';
import { TableWrap, Td, Th, Tr } from '@/components/ui/Table';
import { TaskTable } from '@/components/domain/TaskTable';
import { PriorityBadge, StatusBadge } from '@/components/domain/status';
import { ChartFrame, ChartTable } from '@/components/charts/ChartFrame';
import { LineChart } from '@/components/charts/LineChart';
import { formatRelativeTime } from '@/lib/format';

const THROUGHPUT_SERIES = [
  { key: 'created', label: 'Created', slot: 1 as const },
  { key: 'completed', label: 'Completed', slot: 2 as const },
  { key: 'approved', label: 'Approved', slot: 3 as const },
];

/**
 * The dashboard (§13).
 *
 * Built to be understood at a glance: a developer sees what they should work on
 * next, a director additionally sees what is behind, what is waiting on them and
 * how the team is doing. Everything on it answers a question somebody actually
 * asks when they open the platform.
 */
export function DashboardPage() {
  const { session } = useSession();
  const { data, isPending, error, refetch } = useDashboard();

  if (isPending) return <LoadingBlock label="Loading your dashboard" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data) return null;

  const { developer, director } = data;
  const firstName = session?.user.displayName.split(' ')[0] ?? 'there';

  return (
    <>
      <PageHeader
        title={`Good to see you, ${firstName}`}
        description="Everything assigned to you, and anything that needs attention."
        actions={<LinkButton to="/tasks/new" variant="primary">Create Task</LinkButton>}
      />

      <section aria-label="My work" className="mb-6">
        <h2 className="mb-2.5 text-xs font-semibold tracking-wide text-text-subtle uppercase">My work</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
          <StatTile label="Active" value={developer.stats.activeTasks} icon={<ListChecks aria-hidden className="size-4" />} />
          <StatTile label="Not started" value={developer.stats.pendingTasks} icon={<Clock aria-hidden className="size-4" />} />
          <StatTile
            label="Done this week"
            value={developer.stats.completedThisWeek}
            tone="success"
            icon={<CheckCircle2 aria-hidden className="size-4" />}
          />
          <StatTile
            label="Awaiting review"
            value={developer.stats.awaitingReview}
            icon={<Gavel aria-hidden className="size-4" />}
          />
          <StatTile
            label="Changes asked"
            value={developer.stats.changesRequested}
            tone={developer.stats.changesRequested > 0 ? 'warning' : 'default'}
            icon={<Undo2 aria-hidden className="size-4" />}
          />
          <StatTile
            label="Overdue"
            value={developer.stats.overdue}
            tone={developer.stats.overdue > 0 ? 'critical' : 'default'}
            icon={<AlertTriangle aria-hidden className="size-4" />}
          />
        </div>
      </section>

      <div className="grid gap-5 xl:grid-cols-3">
        <Panel
          className="xl:col-span-2"
          title="My current tasks"
          description="Highest priority first, then whatever is due soonest."
          flush
          actions={
            <LinkButton to="/tasks/mine" size="sm" variant="ghost">
              View all
            </LinkButton>
          }
        >
          <TaskTable
            tasks={developer.currentTasks}
            hideAssignee
            emptyTitle="Nothing assigned to you right now"
            emptyDescription="Create a task to start tracking work, or wait for a director to assign you something."
            emptyAction={
              <LinkButton to="/tasks/new" variant="primary" size="sm">
                Create a task
              </LinkButton>
            }
          />
        </Panel>

        <Panel title="Recent activity" description="Across everything you can see.">
          {developer.recentActivity.length === 0 ? (
            <EmptyState title="Nothing has happened yet" />
          ) : (
            <ol className="space-y-3">
              {developer.recentActivity.slice(0, 10).map((entry) => (
                <li key={entry.id} className="flex gap-2.5 text-sm">
                  <span className="mt-1 size-1.5 shrink-0 rounded-full bg-border-strong" aria-hidden />
                  <div className="min-w-0">
                    <p className="text-text">{entry.summary}</p>
                    <p className="mt-0.5 text-[11px] text-text-subtle">
                      <Link to={`/tasks/${entry.taskReference}`} className="hover:text-accent-text hover:underline">
                        {formatTaskReference(entry.taskReference)} {entry.taskTitle}
                      </Link>
                      {' · '}
                      <time dateTime={entry.createdAt}>{formatRelativeTime(entry.createdAt)}</time>
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Panel>
      </div>

      {director && <DirectorOverview director={director} />}
    </>
  );
}

type DirectorSection = NonNullable<ReturnType<typeof useDashboard>['data']>['director'];

function DirectorOverview({ director }: { director: NonNullable<DirectorSection> }) {
  const { stats, team, reviewQueue, throughput, attentionRequired } = director;

  return (
    <>
      <section aria-label="Development overview" className="mt-8 mb-6">
        <h2 className="mb-2.5 text-xs font-semibold tracking-wide text-text-subtle uppercase">
          Development overview
        </h2>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
          <StatTile label="Active tasks" value={stats.activeTasks} />
          <StatTile
            label="Pending review"
            value={stats.pendingReview}
            tone={stats.pendingReview > 0 ? 'warning' : 'default'}
            hint={stats.pendingReview > 0 ? <Link to="/review" className="hover:underline">Open the queue</Link> : undefined}
          />
          <StatTile label="Completed today" value={stats.completedToday} tone="success" />
          <StatTile label="Overdue" value={stats.overdue} tone={stats.overdue > 0 ? 'critical' : 'default'} />
          <StatTile label="Unassigned" value={stats.unassigned} />
          <StatTile
            label="Critical open"
            value={stats.criticalOpen}
            tone={stats.criticalThresholdExceeded ? 'critical' : 'default'}
            icon={<Flame aria-hidden className="size-4" />}
          />
        </div>

        {stats.criticalThresholdExceeded && (
          <Callout
            tone="warning"
            className="mt-3"
            icon={<Flame aria-hidden className="size-3.5" />}
            title="Critical priority is losing its meaning"
          >
            {stats.criticalOpen} tasks are open at CRITICAL priority. When everything is critical,
            nothing is — review them and downgrade what is not genuinely blocking.
          </Callout>
        )}
      </section>

      <div className="grid gap-5 xl:grid-cols-3">
        <Panel
          className="xl:col-span-2"
          title="Team performance"
          description="Last 30 days."
          flush
          actions={
            <LinkButton to="/team" size="sm" variant="ghost">
              Manage team
            </LinkButton>
          }
        >
          {team.length === 0 ? (
            <EmptyState title="No developers yet" />
          ) : (
            <TableWrap>
              <thead>
                <tr>
                  <Th>Developer</Th>
                  <Th align="right">Active</Th>
                  <Th align="right">Completed</Th>
                  <Th align="right">Overdue</Th>
                  <Th align="right">Avg time</Th>
                  <Th align="right">First-pass</Th>
                </tr>
              </thead>
              <tbody>
                {team.map((row) => (
                  <Tr key={row.user.id}>
                    <Td>
                      <Link to={`/team/${row.user.id}`} className="hover:text-accent-text">
                        <UserChip user={row.user} size="xs" />
                      </Link>
                    </Td>
                    <Td align="right" className="tabular">{row.activeTasks}</Td>
                    <Td align="right" className="tabular">{row.completedTasks}</Td>
                    <Td
                      align="right"
                      className={row.overdueTasks > 0 ? 'font-medium text-critical-tone tabular' : 'tabular'}
                    >
                      {row.overdueTasks}
                    </Td>
                    <Td align="right" className="tabular">{formatHours(row.averageCompletionMs)}</Td>
                    <Td align="right" className="tabular">{formatPercent(row.firstPassApprovalRate)}</Td>
                  </Tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Panel>

        <Panel
          title="Review queue"
          description={`${reviewQueue.length} waiting on a director.`}
          flush
          actions={
            <LinkButton to="/review" size="sm" variant="ghost">
              Open
            </LinkButton>
          }
        >
          {reviewQueue.length === 0 ? (
            <EmptyState title="Nothing waiting" description="Every submitted task has been reviewed." />
          ) : (
            <ul className="divide-y divide-border">
              {reviewQueue.map((task) => (
                <li key={task.id}>
                  <Link
                    to={`/review/${task.reference}`}
                    className="block px-4 py-3 transition-colors hover:bg-surface-hover"
                  >
                    <div className="flex items-center gap-2">
                      <PriorityBadge priority={task.priority} size="sm" />
                      <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-text-subtle tabular">
                        <Timer aria-hidden className="size-3" />
                        {formatDuration(task.waitingMs)}
                      </span>
                    </div>
                    <p className="mt-1.5 truncate text-sm font-medium text-text">{task.title}</p>
                    <p className="mt-0.5 truncate text-[11px] text-text-subtle">
                      {formatTaskReference(task.reference)} · {task.assignee?.displayName ?? 'Unassigned'}
                      {task.previousReviewCycles > 0 && ` · ${task.previousReviewCycles} previous round(s)`}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-3">
        <Panel className="xl:col-span-2">
          <ChartFrame
            title="Throughput"
            description="Tasks created, completed and approved over the last 30 days."
            series={THROUGHPUT_SERIES}
            table={
              <ChartTable
                columns={['Period', 'Created', 'Completed', 'Approved']}
                rows={throughput.map((point) => [
                  point.label,
                  point.values['created'] ?? 0,
                  point.values['completed'] ?? 0,
                  point.values['approved'] ?? 0,
                ])}
              />
            }
          >
            <LineChart points={throughput} series={THROUGHPUT_SERIES} height={210} />
          </ChartFrame>
        </Panel>

        <Panel title="Needs attention" description="Overdue, stalled in review, or critical and unstarted." flush>
          {attentionRequired.length === 0 ? (
            <EmptyState title="Nothing is behind" description="No overdue work and no long review waits." />
          ) : (
            <ul className="divide-y divide-border">
              {attentionRequired.map((task) => (
                <li key={task.id}>
                  <Link to={`/tasks/${task.reference}`} className="block px-4 py-3 transition-colors hover:bg-surface-hover">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <StatusBadge status={task.status} size="sm" />
                      <PriorityBadge priority={task.priority} size="sm" />
                      {task.metrics.isOverdue && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-critical-tone">
                          <AlertTriangle aria-hidden className="size-3" />
                          Overdue by {formatDuration(task.metrics.overdueByMs)}
                        </span>
                      )}
                    </div>
                    <p className="mt-1.5 truncate text-sm text-text">{task.title}</p>
                    <p className="mt-0.5 text-[11px] text-text-subtle">
                      {formatTaskReference(task.reference)} · {task.assignee?.displayName ?? 'Unassigned'}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </>
  );
}
